import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

export default function ForgotPasswordPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1: enter email, 2: enter OTP + new pw
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const [loading, setLoading] = useState(false);

  const sendOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setDevOtp(null);
    try {
      const { data } = await api.post("/auth/forgot-password/send-otp", { email });
      toast.success(data.message || "OTP sent");
      if (data.dev_otp) setDevOtp(data.dev_otp);
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyAndReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/verify", { email, otp, new_password: newPassword });
      toast.success("Password reset — please sign in");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Recover Access</p>
      <h1 className="font-serif text-4xl text-midnight mb-3">Forgot password?</h1>
      <p className="text-sm text-neutral-600 mb-8">
        Enter your registered email and we'll send you a 6-digit OTP to reset your password.
      </p>

      {step === 1 && (
        <form onSubmit={sendOtp} className="space-y-5" data-testid="forgot-pw-step1">
          <div>
            <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">Email</label>
            <Input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              required data-testid="forgot-email-input" placeholder="you@example.com"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-midnight hover:bg-midnight-hover text-bone py-6" data-testid="send-otp-btn">
            {loading ? "Sending..." : "Send OTP"}
          </Button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={verifyAndReset} className="space-y-5" data-testid="forgot-pw-step2">
          {devOtp && (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
              <p className="font-medium mb-1">Dev mode</p>
              <p>Real email service not configured yet. Your OTP: <span className="font-mono text-lg" data-testid="dev-otp">{devOtp}</span></p>
            </div>
          )}
          <div>
            <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">OTP (6 digits)</label>
            <Input
              inputMode="numeric" maxLength={6} value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              required data-testid="otp-input"
              placeholder="123456" className="tracking-widest text-center font-mono text-lg"
            />
          </div>
          <div>
            <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">New Password</label>
            <Input
              type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              required minLength={6} data-testid="new-password-input" placeholder="At least 6 characters"
            />
          </div>
          <div>
            <label className="text-xs tracking-widest uppercase text-neutral-500 block mb-2">Confirm Password</label>
            <Input
              type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              required minLength={6} data-testid="confirm-password-input" placeholder="Re-enter password"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-midnight hover:bg-midnight-hover text-bone py-6" data-testid="reset-password-btn">
            {loading ? "Resetting..." : "Reset Password"}
          </Button>
          <button type="button" onClick={() => { setStep(1); setOtp(""); setDevOtp(null); }} className="text-xs text-neutral-500 underline">
            ← Change email
          </button>
        </form>
      )}

      <div className="mt-6 text-sm text-neutral-600">
        Remembered it?{" "}
        <Link to="/login" className="text-midnight underline hover:text-gold" data-testid="back-to-login">Sign in</Link>
      </div>
    </div>
  );
}
